/**
 * Выбор точки доступа цели по профилю (решение владельца 26.07, паттерн IMDF
 * opening): явные метки access_* сильнее конвенции; без меток — пешеход →
 * входы, фура → рампы, авто → парковка → рампы → входы; честные фолбэки.
 */
import { haversineM, type LonLat } from '@app/geo'
import type { RouteProfile } from './graph'
import {
  DOOR_KINDS,
  nearestAccessNodeId,
  pointInRing,
  sameBuildingUnits,
  unitCentroid,
  unitContaining,
} from './route-geom'
import { isParking } from './taxonomy'
import type { GraphNode, Venue } from './types'

/** Явная метка «кто заходит здесь» на узле входа (дровер двери редактора). */
const ACCESS_ATTR: Record<RouteProfile, string> = {
  foot: 'access_foot',
  wheelchair: 'access_foot',
  vehicle: 'access_vehicle',
  truck: 'access_truck',
}

/** Парковка «этого» здания: паркинг-POI/юниты в радиусе от центра (конвенция). */
const PARKING_NEAR_M = 80

/** Грузовая парковка (attrs.parking_for='truck') — стоянка для фур (R3). */
function isTruckParking(poi: { attrs?: Readonly<Record<string, unknown>> }): boolean {
  return poi.attrs?.['parking_for'] === 'truck'
}

function parkingPoiNodes(venue: Venue, center: LonLat, truckOnly: boolean): string[] {
  const out: string[] = []
  for (const poi of venue.pois) {
    if (!isParking(venue, poi.category) || haversineM(poi.point, center) > PARKING_NEAR_M) continue
    if (truckOnly && !isTruckParking(poi)) continue
    const linked = venue.network.nodes.find((n) => n.poi === poi.id)
    const nodeId = linked?.id ?? nearestAccessNodeId(venue, poi.point, poi.level)
    if (nodeId) out.push(nodeId)
  }
  return out
}

function parkingUnitEntrances(
  venue: Venue,
  unitId: string,
  center: LonLat,
  truckOnly: boolean,
): string[] {
  const out: string[] = []
  for (const pu of venue.units) {
    if (pu.id === unitId || !isParking(venue, pu.category)) continue
    if (truckOnly && !isTruckParking(pu)) continue
    const pc = unitCentroid(pu)
    if (pc && haversineM(pc, center) <= PARKING_NEAR_M) out.push(...(pu.entrances ?? []))
  }
  return out
}

/** Узлы парковки рядом с точкой; truckOnly — только стоянки для фур (R3). */
function parkingNodeIdsNear(
  venue: Venue,
  center: LonLat,
  truckOnly = false,
  excludeUnitId = '',
): string[] {
  return [
    ...new Set([
      ...parkingPoiNodes(venue, center, truckOnly),
      ...parkingUnitEntrances(venue, excludeUnitId, center, truckOnly),
    ]),
  ]
}

/** Узлы парковки здания; truckOnly — только стоянки для фур (R3). */
function parkingNodeIds(venue: Venue, unit: Venue['units'][number], truckOnly = false): string[] {
  const c = unitCentroid(unit)
  return c ? parkingNodeIdsNear(venue, c, truckOnly, unit.id) : []
}

/**
 * Узлы САМОЙ парковки — когда она и есть цель.
 *
 * К парковке едут, а не «паркуются рядом с парковкой»: без этого движок
 * отправлял человека на соседнюю стоянку и обратно пешком (фидбек владельца
 * 29.07). Берём, по убыванию точности: нарисованные въезды юнита → узел
 * парковочной POI внутри его контура → ближайший узел сети.
 */
function ownParkingNodeIds(venue: Venue, unit: Venue['units'][number]): string[] {
  const own = unit.entrances ?? []
  if (own.length > 0) return [...own]
  const inside: string[] = []
  for (const poi of venue.pois) {
    if (!isParking(venue, poi.category) || !pointInUnit(poi, unit)) continue
    const linked = venue.network.nodes.find((n) => n.poi === poi.id)
    if (linked) inside.push(linked.id)
  }
  if (inside.length > 0) return inside
  const near = nearestNodeToUnit(venue, unit)
  return near ? [near] : []
}

/** Относится ли точка к этому юниту (тот же уровень, рядом с центром). */
function pointInUnit(poi: Venue['pois'][number], unit: Venue['units'][number]): boolean {
  if (poi.level !== unit.level) return false
  const c = unitCentroid(unit)
  if (!c) return false
  // Контур парковки редко бывает крупным: 60 м от центра — достаточный признак
  // «эта POI относится к этой парковке», без полноценного point-in-polygon.
  return haversineM(poi.point, c) <= 60
}

/** Ближайший к зданию узел сети — цель, когда у здания не нарисовано дверей. */
function nearestNodeToUnit(venue: Venue, unit: Venue['units'][number]): string | null {
  const c = unitCentroid(unit)
  return c ? nearestAccessNodeId(venue, c, unit.level) : null
}

/** Двери/рампы, НАРИСОВАННЫЕ внутри любого контура группы (геом-фолбэк). */
function geometricDoors(
  venue: Venue,
  group: Venue['units'][number][],
  entranceIds: Set<string>,
  dockIds: Set<string>,
): void {
  for (const u of group) {
    const ring = u.geometry.coordinates[0]
    if (!ring) continue
    for (const n of venue.network.nodes) {
      if (!DOOR_KINDS.has(n.kind) || !pointInRing(n.point, ring)) continue
      if (n.kind === 'dock') dockIds.add(n.id)
      else entranceIds.add(n.id)
    }
  }
}

/**
 * Юниты ОДНОЙ СТОПКИ: тот же след здания, разные этажи. Признак — взаимная
 * вложенность центров: центр каждого лежит внутри контура другого.
 *
 * ПОЧЕМУ НЕ sameBuildingUnits. Та считает «одним зданием» и магазин внутри
 * торгового центра: центр магазина лежит внутри оболочки ТЦ. Для склада в два
 * этажа правило верное (дверь первого этажа обслуживает второй), а для ТЦ —
 * разрушительное: 223 магазина Ülemiste не имеют своих дверей, наследовали
 * два входа оболочки, и маршрут до ЛЮБОГО магазина заканчивался у одного и
 * того же уличного входа (баг владельца 30.07). Вложенность обязана быть
 * взаимной: оболочка ТЦ внутрь магазина не помещается.
 */
function stackedUnits(venue: Venue, unit: Venue['units'][number]): Venue['units'][number][] {
  const ring = unit.geometry.coordinates[0]
  const c = unitCentroid(unit)
  const out = [unit]
  for (const u of venue.units) {
    if (u.id === unit.id) continue
    const r = u.geometry.coordinates[0]
    const uc = unitCentroid(u)
    if (ring && r && c && uc && pointInRing(uc, ring) && pointInRing(c, r)) out.push(u)
  }
  return out
}

/**
 * Добор входов/рамп у здания БЕЗ своих дверей: сначала со стопки одинакового
 * корпуса (вход на версии другого этажа), затем — с СВОЕГО контура (нарисован,
 * но связь не проставлена). Мутирует переданные множества.
 */
function poolStackedDoors(
  venue: Venue,
  unit: Venue['units'][number],
  entranceIds: Set<string>,
  dockIds: Set<string>,
): void {
  for (const u of stackedUnits(venue, unit)) {
    for (const e of u.entrances ?? []) entranceIds.add(e)
    for (const d of u.docks ?? []) dockIds.add(d)
  }
  if (entranceIds.size === 0 && dockIds.size === 0)
    geometricDoors(venue, [unit], entranceIds, dockIds)
}

/**
 * Помещение внутри большего контура (магазин в ТЦ) — и внутри есть сеть на его
 * этаже? Тогда «дверь» такого помещения — ближайший узел прохода на ЕГО этаже:
 * человека ведут через центр к магазину, а не к уличному входу.
 *
 * Возвращает пусто, если сети на этаже нет: тогда работает прежний фолбэк на
 * входы оболочки — довести до здания лучше, чем не довести никуда.
 */
function indoorApproachNodeIds(venue: Venue, unit: Venue['units'][number]): string[] {
  // Оболочка — та, что содержит нас, но не содержится в нас. Этаж-близнец из
  // стопки (взаимная вложенность) оболочкой НЕ является: у склада в два этажа
  // дверь первого обслуживает второй, и подменять её внутренним узлом нельзя.
  const stack = new Set(stackedUnits(venue, unit).map((u) => u.id))
  const envelope = sameBuildingUnits(venue, unit).some((u) => !stack.has(u.id))
  if (!envelope) return []
  const onLevel = venue.network.nodes.some((n) => n.level === unit.level)
  if (!onLevel) return []
  const near = nearestNodeToUnit(venue, unit)
  return near ? [near] : []
}

/**
 * Узлы доступа корпуса (решение владельца 27.07): собираем entrances+docks+back-ref
 * СО ВСЕХ юнитов-этажей одного контура (двери «на другом этаже стопки» считаются).
 * Если привязок нет вовсе — геом-фолбэк: двери/рампы, НАРИСОВАННЫЕ внутри контура
 * (владелец нарисовал вход, но связь не проставлена). Геометрия = принадлежность
 * (паттерн IMDF Opening). Возвращает узлы + множества id входов и рамп.
 */
function buildingAccess(
  venue: Venue,
  unit: Venue['units'][number],
): { nodes: GraphNode[]; entranceIds: Set<string>; dockIds: Set<string> } {
  const entranceIds = new Set<string>(unit.entrances ?? [])
  const dockIds = new Set<string>(unit.docks ?? [])
  const backref = venue.network.nodes.filter((n) => n.unit === unit.id).map((n) => n.id)
  // ВАЖНО: добор со стопки/контура — ТОЛЬКО если у юнита нет СВОИХ входов. Иначе
  // в ТЦ (этажи — разные магазины с общим контуром) подмешались бы чужие двери.
  if (entranceIds.size === 0 && dockIds.size === 0 && backref.length === 0) {
    poolStackedDoors(venue, unit, entranceIds, dockIds)
  }
  const ids = new Set<string>([...entranceIds, ...dockIds, ...backref])
  return { nodes: venue.network.nodes.filter((n) => ids.has(n.id)), entranceIds, dockIds }
}

/**
 * Узлы-двери цели по профилю (решение владельца 26.07, паттерн IMDF opening):
 * 1) ЯВНЫЕ метки на узлах (attrs.access_foot/vehicle/truck из дровера двери)
 *    всегда сильнее конвенции — «в ручную всё поставить» работает точно;
 * 2) без меток — понятная конвенция: пешеход → входы; фура → рампы (docks);
 *    авто → парковка здания (паркинг рядом) → рампы → входы;
 * 3) честные фолбэки: если есть только «Вход» — все едут к нему, никто
 *    не кружит вокруг здания. Для POI — его узел / ближайший на уровне.
 */
/**
 * Главный вход (запрос владельца 30.07).
 *
 * ПОЧЕМУ ЭТО НУЖНО. Без метки маршрут ведёт в ближайшую дверь, а «ближайшая» и
 * «та, куда людям надо» — разные двери: у торгового центра это служебный вход
 * со стороны путей вместо центрального от парковки. Геометрия такого знать не
 * может, знает только владелец.
 *
 * ПРАВИЛО РОВНО ТАКОЕ, КАК ПРОСИЛ ВЛАДЕЛЕЦ: по умолчанию вход обычный; если
 * главный не отмечен ни один — все равны и работает прежний выбор по
 * расстоянию; если отмечен хоть один — маршрут ведёт только в отмеченные.
 * Поэтому пустой список здесь означает «правило не применяется», а не «входов
 * нет»: молча оставить человека без маршрута хуже, чем провести не в ту дверь.
 */
export function isPrimaryEntrance(node: { attrs?: Readonly<Record<string, unknown>> }): boolean {
  return node.attrs?.['primary'] === true
}

/** Отобрать главные входы из набора; пусто — главных не назначено. */
function primaryAmong(
  nodes: readonly GraphNode[],
  ids: ReadonlySet<string> | readonly string[],
): string[] {
  const set = ids instanceof Set ? ids : new Set(ids)
  return nodes.filter((n) => set.has(n.id) && isPrimaryEntrance(n)).map((n) => n.id)
}

export function unitTargetNodeIds(
  venue: Venue,
  unit: Venue['units'][number],
  profile: RouteProfile,
): string[] {
  const { nodes, entranceIds, dockIds } = buildingAccess(venue, unit)
  const explicit = nodes.filter((n) => n.attrs?.[ACCESS_ATTR[profile]] === true).map((n) => n.id)
  if (explicit.length > 0) return explicit
  // Главные входы сужают набор: назначен хоть один — ведём только в него.
  const primary = primaryAmong(nodes, entranceIds)
  const entrances = primary.length > 0 ? primary : [...entranceIds]
  const docks = [...dockIds]
  const backref = nodes.map((n) => n.id)
  // Коляска: входы с пандусом (attrs.wheelchair_ramp) приоритетнее прочих.
  if (profile === 'wheelchair') {
    const ramped = nodes
      .filter((n) => entranceIds.has(n.id) && n.attrs?.['wheelchair_ramp'] === true)
      .map((n) => n.id)
    if (ramped.length > 0) return ramped
  }
  // Пешеходу и коляске к магазину БЕЗ своей двери ведём ЧЕРЕЗ ЦЕНТР — к
  // ближайшему проходу на его этаже, а не к уличному входу оболочки. Своя
  // дверь, если она есть, всё равно сильнее. Транспорту наоборот: машина
  // внутрь не заедет, ей нужен вход, рампа или парковка.
  const indoor =
    profile === 'foot' || profile === 'wheelchair' ? indoorApproachNodeIds(venue, unit) : []
  const chain =
    profile === 'truck'
      ? [docks, entrances, backref]
      : profile === 'vehicle'
        ? [parkingNodeIds(venue, unit), docks, entrances, backref]
        : [entrances, indoor, docks, backref]
  return chain.find((c) => c.length > 0) ?? []
}

/**
 * Двери-входы юнита ПО ПРОФИЛЮ, без парковки (для «внутрь только через вход»):
 * явные метки access_* → входы (коляска — с пандусом первыми) → рампы → back-ref.
 * Входы/рампы собираются со ВСЕЙ стопки корпуса + геом-фолбэк (buildingAccess).
 * Отличается от unitTargetNodeIds тем, что НЕ подмешивает парковку.
 */
export function entranceDoorIds(
  venue: Venue,
  unit: Venue['units'][number],
  profile: RouteProfile,
): string[] {
  const { nodes, entranceIds, dockIds } = buildingAccess(venue, unit)
  const explicit = nodes.filter((n) => n.attrs?.[ACCESS_ATTR[profile]] === true).map((n) => n.id)
  if (explicit.length > 0) return explicit
  if (profile === 'wheelchair') {
    const ramped = nodes
      .filter((n) => entranceIds.has(n.id) && n.attrs?.['wheelchair_ramp'] === true)
      .map((n) => n.id)
    if (ramped.length > 0) return ramped
  }
  // Своя дверь сильнее внутреннего прохода: владелец нарисовал её не зря.
  if (entranceIds.size > 0) {
    const primary = primaryAmong(nodes, entranceIds)
    return primary.length > 0 ? primary : [...entranceIds]
  }
  const indoor =
    profile === 'foot' || profile === 'wheelchair' ? indoorApproachNodeIds(venue, unit) : []
  if (indoor.length > 0) return indoor
  if (dockIds.size > 0) return [...dockIds]
  return nodes.map((n) => n.id)
}

/** Этап прибытия: группа узлов-целей + профиль движения к ним. */
export interface ArrivalLeg {
  readonly nodeIds: readonly string[]
  readonly profile: RouteProfile
  /** Парковки-кандидаты: если к цели нет проезда, авто/фура паркуется на
   *  ближайшей достижимой и дальше пешком (park-and-walk без здания). */
  readonly parkNodeIds?: readonly string[]
}

/**
 * Цепочка «заехал на парковку → пешком до двери» (решение владельца 27.07):
 * ДРАЙВ к ПАРКОВКЕ рисуется сплошной (машина/фура заезжает), от парковки —
 * пунктиром пешком до двери. Отдаём ВСЕ парковки и ВСЕ двери здания —
 * ОПТИМАЛЬНУЮ пару (парковка, дверь) выбирает граф-слой (parkAndWalkRoute),
 * минимизируя `drive + γ·walk` по реальной сети, а не по прямой. Нет
 * парковки → ведём как пешехода к двери.
 */
function driveThenWalkLegs(
  venue: Venue,
  host: Venue['units'][number],
  driveProfile: 'vehicle' | 'truck',
  parking: string[],
): ArrivalLeg[] {
  const doors = entranceDoorIds(venue, host, 'foot')
  if (parking.length === 0) {
    // Негде оставить машину: есть двери → как пешеход к двери; нет дверей —
    // ведём к ближайшему узлу сети У ЭТОГО здания (дальше короткий пунктир к
    // стене). Без этого плечо было пустым, и фолбэк тянул длинную прямую через
    // соседний корпус (фидбек владельца 27.07: SKL1k «сквозь Склад B»).
    if (doors.length > 0) return [{ nodeIds: doors, profile: 'foot' }]
    const near = nearestNodeToUnit(venue, host)
    return near ? [{ nodeIds: [near], profile: 'foot' }] : []
  }
  if (doors.length === 0) {
    // Парковка есть, но дверь НЕ нарисована (запрос владельца 27.07): всё равно
    // ЗАЕЗЖАЕМ на парковку (сплошная), дальше пунктиром к стене — «хотя бы
    // паркуемся рядом», а не игнорируем парковку. Отход к стене — undrawnEntrancePoint.
    return [{ nodeIds: parking, profile: driveProfile }]
  }
  return [
    { nodeIds: parking, profile: driveProfile },
    { nodeIds: doors, profile: 'foot' },
  ]
}

/** Фура: рампа → грузовая парковка → грузовая дверь → ближайший узел (R3). */
function truckArrivalLegs(venue: Venue, host: Venue['units'][number]): ArrivalLeg[] {
  // Рампа со ВСЕЙ стопки корпуса (buildingAccess), а не только host.docks —
  // владелец нарисовал рампу на версии другого этажа (запрос 27.07).
  const { nodes, dockIds } = buildingAccess(venue, host)
  const explicitTruck = nodes.filter((n) => n.attrs?.['access_truck'] === true).map((n) => n.id)
  if (explicitTruck.length > 0) return [{ nodeIds: explicitTruck, profile: 'truck' }]
  if (dockIds.size > 0) return [{ nodeIds: [...dockIds], profile: 'truck' }]
  // Рампы нет (R3, «г»): есть грузовая парковка → фура ЗАЕЗЖАЕТ на неё
  // (сплошная), дальше пешком до двери (пунктир); иначе — к грузовой двери.
  const truckParking = parkingNodeIds(venue, host, true)
  if (truckParking.length > 0) return driveThenWalkLegs(venue, host, 'truck', truckParking)
  const truckDoors = entranceDoorIds(venue, host, 'truck')
  if (truckDoors.length > 0) return [{ nodeIds: truckDoors, profile: 'truck' }]
  // Ни рампы, ни двери, ни грузовой парковки → к ближайшему узлу у здания.
  const nearTruck = nearestNodeToUnit(venue, host)
  return nearTruck ? [{ nodeIds: [nearTruck], profile: 'truck' }] : []
}

/**
 * Прибытие к арендатору ВНУТРИ здания host (park-and-walk, паттерн Google/Apple
 * arrival-point + IMDF opening, research/19): «внутрь только через вход».
 * — пешеход/коляска: сразу к ближайшей двери (пандус — приоритет);
 * — фура: рампа (грузовой вход внутрь) → нет рампы → грузовая парковка → дверь;
 * — авто: ДРАЙВ до парковки (сплошная) → ПЕШКОМ до двери (пунктир).
 */
function poiArrivalLegs(
  venue: Venue,
  host: Venue['units'][number],
  profile: RouteProfile,
): ArrivalLeg[] {
  // ЦЕЛЬ — САМА ПАРКОВКА. Park-and-walk тут бессмыслен: он искал бы, где
  // припарковаться рядом с парковкой, и уводил на соседнюю стоянку, откуда
  // человек шёл пешком обратно (критический баг, фидбек владельца 29.07).
  if (isParking(venue, host.category)) {
    const own = ownParkingNodeIds(venue, host)
    return own.length > 0 ? [{ nodeIds: own, profile }] : []
  }
  if (profile === 'truck') return truckArrivalLegs(venue, host)
  if (profile === 'vehicle') {
    return driveThenWalkLegs(venue, host, 'vehicle', parkingNodeIds(venue, host))
  }
  const doors = entranceDoorIds(venue, host, profile)
  if (doors.length > 0) return [{ nodeIds: doors, profile }]
  // Дверей нет вовсе → к ближайшему узлу сети у здания (короткий пунктир к
  // стене), а не пустое плечо с длинной прямой через соседний корпус.
  const near = nearestNodeToUnit(venue, host)
  return near ? [{ nodeIds: [near], profile }] : []
}

/**
 * Упорядоченные этапы прибытия к цели (мультимодальный park-and-walk).
 * Один этап — обычная одно-профильная цель (здание/парковка/узел). Несколько —
 * авто до парковки, затем пешком до двери арендатора внутри здания.
 */
/** Этапы прибытия к отдельной POI (парковка/КПП/точка вне здания). */
function poiTargetLegs(
  venue: Venue,
  poi: Venue['pois'][number],
  profile: RouteProfile,
): ArrivalLeg[] {
  const linked = venue.network.nodes.find((n) => n.poi === poi.id)
  // POI ВНУТРИ здания (без своего узла) → park-and-walk через двери здания.
  const host = linked ? null : unitContaining(venue, poi)
  if (host) return poiArrivalLegs(venue, host, profile)
  const targetNode = linked?.id ?? nearestAccessNodeId(venue, poi.point, poi.level)
  if (!targetNode) return []
  // Авто/фура: прямой доезд к цели, а если проезда нет — паркуемся на БЛИЖАЙШЕЙ
  // достижимой парковке и пешком (как к зданию: «через Парковка», запрос 27.07).
  // К парковке едут напрямую: «припарковаться рядом с парковкой» — бессмыслица.
  if ((profile === 'vehicle' || profile === 'truck') && !isParking(venue, poi.category)) {
    const parkings = parkingNodeIdsNear(venue, poi.point, profile === 'truck')
    if (parkings.length > 0) return [{ nodeIds: [targetNode], profile, parkNodeIds: parkings }]
  }
  return [{ nodeIds: [targetNode], profile }]
}

export function arrivalLegs(
  venue: Venue,
  targetId: string,
  profile: RouteProfile = 'foot',
): ArrivalLeg[] {
  const unit = venue.units.find((u) => u.id === targetId)
  // Здание как цель — та же цепочка, что для арендатора внутри (авто →
  // парковка → пешком до двери; фура → рампа; пешеход → дверь).
  if (unit) return poiArrivalLegs(venue, unit, profile)
  const poi = venue.pois.find((p) => p.id === targetId)
  if (poi) return poiTargetLegs(venue, poi, profile)
  if (venue.network.nodes.some((n) => n.id === targetId)) return [{ nodeIds: [targetId], profile }]
  return []
}
